<template>
  <div :class="[$style.panel, { [$style.mini]: isMini }]">
    <TenantOrgaSelect v-if="!isMini && !isDesktop" :class="$style.tenantOrgaSelect" />
    <div :class="$style.sidebarNavTabs">
      <SidebarNavHeader
        v-model="searchTerm"
        :is-mini="isMini"
        :show-settings="true"
        :nodes="allNodes"
        :set-all-expanded="setAllExpanded"
        :all-expanded="isAllExpanded"
        :all-collapsed="isAllCollapsed"
      />
    </div>

    <div :class="$style.innerPanel">
      <div :class="$style.mainMenuSection">
        <div v-show="!isMini">
          <SidebarNavDefault :nodes="currentNodes" :search-term="searchTerm" />
        </div>
        <div v-show="isMini">
          <SidebarNavMini :nodes="currentNodes" />
        </div>
      </div>
    </div>
  </div>

  <div
    v-if="!isDesktop"
    :class="$style.sidebarNavBackground"
    @touchmove.prevent.self
    @click="layoutStore.toggleBarVisibility('navigation')"
  />
</template>

<script setup lang="ts">
import { TenantType } from "@scayla/api-client-fetch";
import { useScaBreakpoints } from "@scayla/core";
import { computed, ref, watch } from "vue";
import { onBeforeRouteUpdate } from "vue-router";

import { useLayoutStore } from "@/store/layoutStore";
import { useWorkspace } from "@/store/workspace";
import type { NavNode } from "@/utils/navUtils";

import { useAccountingNav } from "./accounting";
import { filterNavTreeForFavorites } from "./favorites";
import { useHomeNav } from "./home";
import { useSettingsNav } from "./settings";
import SidebarNavDefault from "./sidebarNav/SidebarNavDefault.vue";
import SidebarNavHeader from "./sidebarNav/SidebarNavHeader.vue";
import SidebarNavMini from "./sidebarNav/SidebarNavMini.vue";
import { useTenantManagementNav } from "./tenantManagement";
import TenantOrgaSelect from "./TenantOrgaSelect.vue";
import { useUserSettingsNav } from "./userSettings";

const props = defineProps<{
  allModuleNodes: NavNode[];
  favoriteModuleNodes: NavNode[];
  binNav: NavNode | null;
}>();

const searchTerm = ref("");
const layoutStore = useLayoutStore();
const workspace = useWorkspace();

const homeNav = useHomeNav();
const accountingNav = useAccountingNav();
const tenantManagementNav = useTenantManagementNav();
const settingsNav = useSettingsNav();
const userSettingsNav = useUserSettingsNav();

const { isDesktop } = useScaBreakpoints();

const isMini = computed(() => layoutStore.sidebarNavMode === "mini");

const filteredHomeNav = computed(() => filterNavTreeForFavorites(homeNav.value, layoutStore));
const filteredAccountingNav = computed(() => filterNavTreeForFavorites(accountingNav.value, layoutStore));
const filteredSettingsNav = computed(() => filterNavTreeForFavorites(settingsNav.value, layoutStore));
const filteredUserSettingsNav = computed(() => filterNavTreeForFavorites(userSettingsNav.value, layoutStore));
const filteredTenantManagementNav = computed(() => filterNavTreeForFavorites(tenantManagementNav.value, layoutStore));

const allNodes = computed<NavNode[]>(() => {
  return [
    homeNav.value,
    ...((workspace.tenantId || workspace.orgId) && workspace.tenant?.type === TenantType.MANDATOR_MANAGEMENT
      ? [tenantManagementNav.value]
      : []),
    ...props.allModuleNodes,
    ...((workspace.tenantId || workspace.orgId) && workspace.tenant?.accountingEnabled ? [accountingNav.value] : []),
    ...(workspace.tenantId || workspace.orgId ? [settingsNav.value] : []),
    userSettingsNav.value,
    props.binNav,
  ].filter((node): node is NavNode => node !== null && node !== undefined);
});

const favoriteNodes = computed<NavNode[]>(() => {
  return [
    filteredHomeNav.value,
    ...((workspace.tenantId || workspace.orgId) && workspace.tenant?.type === TenantType.MANDATOR_MANAGEMENT
      ? [filteredTenantManagementNav.value]
      : []),
    ...props.favoriteModuleNodes,
    ...(workspace.tenantId && workspace.orgId && workspace.tenant?.accountingEnabled
      ? [filteredAccountingNav.value]
      : []),
    ...(workspace.tenantId || workspace.orgId ? [filteredSettingsNav.value] : []),
    filteredUserSettingsNav.value,
  ].filter((node): node is NavNode => node !== null && node !== undefined);
});

const currentNodes = computed<NavNode[]>(() => {
  const isFavorites = layoutStore.sidebarNavContent === "favorites";
  return isFavorites ? favoriteNodes.value : allNodes.value;
});

const allNodeIds = computed<string[]>(() => {
  const ids: string[] = [];

  function collectIds(nodes: NavNode[], parentPath = "") {
    nodes.forEach((node) => {
      if (node.layoutStoreKey) {
        const currentPath = parentPath ? `${parentPath}.${node.layoutStoreKey}` : node.layoutStoreKey;
        ids.push(currentPath);

        if (node.children) {
          collectIds(node.children, currentPath);
        }
      } else if (node.children) {
        collectIds(node.children, parentPath);
      }
    });
  }

  collectIds(allNodes.value);

  return ids;
});

const layoutStoreItemConfigs = computed(() => layoutStore.navItemConfigs);

const isAllExpanded = computed(() =>
  allNodeIds.value.every((id) => layoutStoreItemConfigs.value[id]?.expanded !== false),
);
const isAllCollapsed = computed(() =>
  allNodeIds.value.every((id) => layoutStoreItemConfigs.value[id]?.expanded === false),
);

function setAllExpanded(expanded: boolean) {
  layoutStore.batchSetExpanded(allNodeIds.value, expanded);
}

onBeforeRouteUpdate(() => {
  if (isDesktop.value) return;
  if (isMini.value) return;
  if (!layoutStore.bars.navigation.visible) return;

  layoutStore.setBarVisibility("navigation", false);
});

watch(isMini, () => {
  searchTerm.value = "";
});

watch(
  allNodes,
  (nodes) => {
    layoutStore.setNavigationNodes(nodes);
  },
  { immediate: true, deep: true },
);
</script>

<style lang="scss" module>
.panel {
  display: grid;
  grid-template-rows: min-content min-content auto;
  width: 100%;
  height: 100%;
  overflow: hidden;
  background-color: var(--color-background-default);

  @screen 2xl {
    grid-template-rows: min-content auto;
  }

  &.mini {
    .sidebarNavTabs {
      padding: var(--dimension-100) var(--dimension-50);
      gap: var(--space-component-gap-small);
      padding-bottom: 0;
      border-bottom: 0;
      height: auto;
    }

    .sidebarNavInner {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: var(--space-component-gap-medium);
    }

    .mainMenuSection {
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: 0;
    }
  }

  .tenantOrgaSelect {
    padding: 10px var(--space-component-padding-xSmall);
    background-color: var(--color-background-neutral-onSubtle);
  }

  .innerPanel {
    overflow: auto;
    padding: var(--space-component-padding-small) 0;
  }

  .mainMenuSection {
    padding: 0 var(--space-component-padding-medium);
  }
}

.sidebarNavBackground {
  position: fixed;
  overflow: hidden;
  z-index: -2;
  background-color: rgba(0, 0, 0, 0.5);
  top: var(--sca-main-header-height);
  bottom: 0;
  left: 0;
  right: 0;
}

.sidebarNavTabs {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  height: var(--dimension-550);
  gap: var(--space-component-gap-xLarge);
  width: 100%;
  padding: var(--dimension-100) var(--dimension-150);
  border-bottom: solid 1px var(--color-border-default);
}

.enterFrom {
  opacity: 0;
  transform: translateX(12px);
}
.enterTo {
  opacity: 1;
  transform: translateX(0);
}
.leaveFrom {
  opacity: 1;
  transform: translateY(0);
}
.leaveTo {
  opacity: 0;
  transform: translateX(12px);
}
.enterActive,
.leaveActive {
  transition:
    opacity 0.1s ease,
    transform 0.1s ease;
}
</style>
